import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Coins, Eye, Search, ShieldCheck, UserMinus, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { adminAPI, profitSharingAPI, stewardAPI, unitAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { hasAdminPermission } from '../utils/adminPermissions';
import {
  calculateComplianceRate,
  formatIntegerCurrency,
  formatIntegerNumber,
  formatNumber,
  getDaysElapsedInMonth,
  getUserDisplayName,
  getUserPhone,
  isSameMonth
} from '../utils/operations';

function getRoleBadge(role, isActive = false) {
  const styles = {
    'Super-Admin': 'bg-red-50 text-red-700 border-red-200',
    Admin: 'bg-orange-50 text-orange-700 border-orange-200',
    GM: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    Finance: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Business: 'bg-blue-50 text-blue-700 border-blue-200',
    AfterSales: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    RP: 'bg-purple-50 text-purple-700 border-purple-200',
    KUD: 'bg-cyan-50 text-cyan-800 border-cyan-300',
    PUSKUD: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Steward: 'bg-amber-50 text-amber-700 border-amber-200',
    User: 'bg-slate-50 text-slate-700 border-slate-200'
  };

  return `rounded-lg border px-2.5 py-1 text-xs font-bold ${styles[role] || 'bg-slate-50 text-slate-700 border-slate-200'} ${isActive ? 'ring-2 ring-cyan-300 ring-offset-2 ring-offset-white' : ''}`;
}

function MetricCard({ icon: Icon, label, value, accent = 'cyan' }) {
  const accentStyles = {
    cyan: 'border-cyan-300/25 bg-cyan-500/10 text-cyan-100',
    emerald: 'border-emerald-300/25 bg-emerald-500/10 text-emerald-100',
    amber: 'border-amber-300/25 bg-amber-500/10 text-amber-100',
    purple: 'border-violet-300/25 bg-violet-500/10 text-violet-100'
  };

  return (
    <div className={`rounded-xl border p-5 ${accentStyles[accent] || accentStyles.cyan}`}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-bold uppercase text-slate-200">{label}</span>
        <Icon size={18} className="shrink-0" />
      </div>
      <div className="mt-4 break-words text-2xl font-black text-white sm:text-3xl">{value}</div>
    </div>
  );
}

export default function RPManagementTable() {
  const { t } = useTranslation();
  const currentAdmin = useAuthStore((state) => state.user);
  const canViewPartnerNetwork = hasAdminPermission(currentAdmin, 'view_partner_network');
  const canReviewCheckins = hasAdminPermission(currentAdmin, 'review_steward_checkins');
  const canManagePartnerBindings = hasAdminPermission(currentAdmin, 'manage_partner_bindings');
  const [rps, setRps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [removingId, setRemovingId] = useState(null);

  const translateRole = (role) => t(`userManagement.roles.${role}`, { defaultValue: role });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      const [usersRes, unitsRes, treeRes, summaryRes, historyRes, complianceRes] = await Promise.all([
        adminAPI.getUsersAllPages({ role: 'KUD' }),
        unitAPI.getUnits(),
        adminAPI.getPartnerTree(),
        canViewPartnerNetwork
          ? profitSharingAPI.getRoleSummary({ role: 'KUD' })
          : Promise.resolve({ data: { data: { roles: [] } } }),
        canReviewCheckins ? stewardAPI.getCheckInHistory({ limit: 500 }) : Promise.resolve({ data: { data: [] } }),
        canViewPartnerNetwork ? stewardAPI.getComplianceSummary() : Promise.resolve({ data: { data: { stewards: [] } } })
      ]);

      const users = usersRes.data?.data || [];
      const units = unitsRes.data?.data || [];
      const partnerTree = treeRes.data?.data || [];
      const roleSummary = summaryRes.data?.data?.roles || [];
      const history = historyRes.data?.data || [];
      const complianceByStewardId = Object.fromEntries(
        (complianceRes.data?.data?.stewards || []).map((entry) => [String(entry.stewardId), entry])
      );
      const daysElapsed = getDaysElapsedInMonth();

      const summaryByUserId = {};
      roleSummary.forEach((entry) => {
        summaryByUserId[String(entry.userId)] = entry;
      });

      const result = users.map((user) => {
        const userId = String(user.id);
        const tree = partnerTree.find((entry) => String(entry.id) === userId) || { children: [] };
        const stewards = tree.children || [];
        const managedUnits = units.filter((unit) => String(unit.rpOwnerId) === userId);
        const subordinateIds = stewards.map((steward) => String(steward.id));
        const monthCheckins = history.filter(
          (record) => subordinateIds.includes(String(record.stewardId)) && isSameMonth(record.createdAt)
        );
        const summary = summaryByUserId[userId] || {};
        const complianceValues = stewards.map((steward) => {
          const stewardUnits = units.filter((unit) => String(unit.stewardId) === String(steward.id));
          const completedCheckins = Number(
            complianceByStewardId[String(steward.id)]?.completedCheckins
            ?? monthCheckins.filter((record) => String(record.stewardId) === String(steward.id)).length
          );
          return calculateComplianceRate({
            completedCheckins,
            unitCount: stewardUnits.length,
            daysElapsed
          });
        });

        return {
          ...user,
          tree,
          stewards,
          managedUnits,
          summary,
          avgCompliance: complianceValues.length
            ? Math.round(complianceValues.reduce((sum, value) => sum + value, 0) / complianceValues.length)
            : 0
        };
      });

      setRps(result);
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('rpManagement.errors.load'));
    } finally {
      setLoading(false);
    }
  };

  const filteredRps = rps.filter((rp) => {
    const search = searchTerm.trim().toLowerCase();
    if (!search) return true;

    const roles = Array.isArray(rp.roles) ? rp.roles : [rp.role].filter(Boolean);
    return (
      getUserDisplayName(rp).toLowerCase().includes(search) ||
      getUserPhone(rp).includes(search) ||
      roles.some((role) => String(role).toLowerCase().includes(search)) ||
      rp.managedUnits.some((unit) => String(unit.deviceId || '').toLowerCase().includes(search))
    );
  });

  const totalStewards = rps.reduce((sum, rp) => sum + rp.stewards.length, 0);
  const totalProfit = rps.reduce((sum, rp) => sum + Number(rp.summary?.totalProfit || 0), 0);
  const avgCompliance = rps.length ? Math.round(rps.reduce((sum, rp) => sum + rp.avgCompliance, 0) / rps.length) : 0;

  const getRoles = (rp) => (
    Array.isArray(rp.roles) && rp.roles.length > 0
      ? rp.roles
      : [rp.role].filter(Boolean)
  );

  const handleRemoveRpRole = async (rp) => {
    if (!canManagePartnerBindings) return;
    const name = getUserDisplayName(rp);
    if (!window.confirm(t('rpManagement.removeRole.confirm', { name }))) {
      return;
    }

    setRemovingId(rp.id);
    try {
      const res = await adminAPI.removeUserRole(rp.id, 'KUD');
      const unboundUnits = res.data?.data?.unboundUnits ?? 0;
      alert(t('rpManagement.removeRole.success', { count: formatNumber(unboundUnits) }));
      await loadData();
    } catch (err) {
      alert(err.response?.data?.message || err.message || t('rpManagement.removeRole.failed'));
    } finally {
      setRemovingId(null);
    }
  };

  const renderRoleBadges = (rp) => (
    <div className="flex flex-wrap gap-1.5">
      {getRoles(rp).map((role) => (
        <span key={`${rp.id}-${role}`} className={getRoleBadge(role, rp.activeRole === role)}>
          {translateRole(role)}
        </span>
      ))}
    </div>
  );

  const renderStewardChips = (rp) => (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {rp.stewards.slice(0, 3).map((steward) => (
        <Link
          key={steward.id}
          to={`/stewards/${steward.id}`}
          className="admin-muted-panel rounded-lg px-2.5 py-1 text-xs font-semibold transition hover:border-cyan-300 hover:text-cyan-700"
        >
          {steward.name}
        </Link>
      ))}
      {rp.stewards.length > 3 && (
        <span className="admin-muted-panel rounded-lg px-2.5 py-1 text-xs font-semibold">
          +{rp.stewards.length - 3}
        </span>
      )}
    </div>
  );

  const renderUnitChips = (rp) => (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {rp.managedUnits.slice(0, 3).map((unit) => (
        <Link
          key={unit.id}
          to={`/units/${unit.id}`}
          className="admin-muted-panel rounded-lg px-2.5 py-1 text-xs font-semibold transition hover:border-cyan-300 hover:text-cyan-700"
        >
          {unit.deviceId}
        </Link>
      ))}
      {rp.managedUnits.length > 3 && (
        <span className="admin-muted-panel rounded-lg px-2.5 py-1 text-xs font-semibold">
          +{rp.managedUnits.length - 3}
        </span>
      )}
    </div>
  );

  const renderActions = (rp, fullWidth = false) => (
    <div className={fullWidth ? 'grid grid-cols-2 gap-2' : 'flex flex-wrap justify-end gap-2'}>
      <Link
        to={`/pa/${rp.id}`}
        className="admin-primary-button inline-flex min-h-10 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-black transition focus:outline-none focus:ring-2 focus:ring-cyan-200"
      >
        <Eye size={16} />
        {t('rpManagement.viewDetail')}
      </Link>
      {canManagePartnerBindings && <button
        type="button"
        onClick={() => handleRemoveRpRole(rp)}
        disabled={removingId === rp.id}
        className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-sm font-black text-rose-700 transition hover:bg-rose-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-rose-200 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <UserMinus size={16} />
        {removingId === rp.id ? t('rpManagement.removeRole.processing') : t('rpManagement.removeRole.action')}
      </button>}
    </div>
  );

  return (
    <div className="admin-text-primary space-y-6 p-4 sm:p-6 lg:p-8">
      <header className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
        <div className="min-w-0">
          <h2 className="flex items-center gap-3 text-2xl font-black sm:text-3xl">
            <Briefcase className="shrink-0 text-cyan-300" size={30} />
            <span className="break-words">{t('rpManagement.title')}</span>
          </h2>
          <p className="admin-text-secondary mt-2 max-w-4xl text-sm leading-6">
            {t('rpManagement.subtitle')}
          </p>
        </div>

        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={t('rpManagement.searchPlaceholder')}
            className="w-full rounded-lg border border-[var(--admin-border-subtle)] bg-[var(--admin-input-bg)] py-3 pl-11 pr-4 text-sm text-[var(--admin-input-text)] outline-none transition placeholder:text-[var(--admin-input-placeholder)] focus:border-cyan-300 focus:ring-2 focus:ring-cyan-400/20"
          />
        </div>
      </header>

      {error && <div className="rounded-lg border border-rose-400/40 bg-rose-950/50 px-4 py-3 text-sm text-rose-100">{error}</div>}

      <section className={`grid gap-4 md:grid-cols-2 ${canViewPartnerNetwork ? 'xl:grid-cols-4' : 'xl:grid-cols-3'}`}>
        <MetricCard icon={Briefcase} label={t('rpManagement.metrics.rps')} value={formatNumber(rps.length)} accent="cyan" />
        <MetricCard icon={Users} label={t('rpManagement.metrics.stewards')} value={formatNumber(totalStewards)} accent="emerald" />
        {canViewPartnerNetwork && (
          <MetricCard icon={Coins} label={t('rpManagement.metrics.monthProfit')} value={formatIntegerCurrency(totalProfit)} accent="amber" />
        )}
        {canViewPartnerNetwork && <MetricCard icon={ShieldCheck} label={t('rpManagement.metrics.avgCompliance')} value={`${avgCompliance}%`} accent="purple" />}
      </section>

      <section className="admin-card hidden overflow-x-auto rounded-xl lg:block">
        <table className={`w-full text-left ${canViewPartnerNetwork ? 'min-w-[1040px]' : 'min-w-[870px]'}`}>
          <thead className="bg-[var(--admin-surface-soft)] text-xs font-black uppercase text-[var(--admin-text-muted)]">
            <tr>
              <th className="p-5">{t('rpManagement.table.user')}</th>
              <th className="p-5">{t('rpManagement.table.stewards')}</th>
              <th className="p-5">{t('rpManagement.table.devices')}</th>
              {canViewPartnerNetwork && <th className="p-5">{t('rpManagement.table.monthProfit')}</th>}
              {canViewPartnerNetwork && <th className="p-5">{t('rpManagement.table.compliance')}</th>}
              <th className="sticky right-0 z-10 bg-[var(--admin-surface-soft)] p-5 text-right shadow-[-12px_0_18px_rgba(15,23,42,0.08)]">{t('rpManagement.table.actions')}</th>
            </tr>
          </thead>
          <tbody className="text-sm text-[var(--admin-text-secondary)]">
            {loading && (
              <tr>
                <td colSpan={4 + (canViewPartnerNetwork ? 2 : 0)} className="p-12 text-center text-[var(--admin-text-muted)]">{t('rpManagement.loading')}</td>
              </tr>
            )}

            {!loading && filteredRps.length === 0 && (
              <tr>
                <td colSpan={4 + (canViewPartnerNetwork ? 2 : 0)} className="p-12 text-center text-[var(--admin-text-muted)]">{t('rpManagement.empty')}</td>
              </tr>
            )}

            {!loading && filteredRps.map((rp) => (
              <tr key={rp.id} className="border-t border-[var(--admin-border-subtle)] transition-colors hover:bg-[var(--admin-surface-soft)]">
                <td className="min-w-[280px] p-5">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-cyan-100 font-black uppercase text-cyan-800">
                      {getUserDisplayName(rp).slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-mono text-xs text-[var(--admin-text-muted)]">ID: {rp.id}</p>
                      <Link
                        to={`/pa/${rp.id}`}
                        className="font-bold text-[var(--admin-text-primary)] transition-colors hover:text-cyan-700 hover:underline"
                      >
                        {getUserDisplayName(rp)}
                      </Link>
                      <p className="text-xs text-[var(--admin-text-muted)]">{getUserPhone(rp)}</p>
                      <div className="mt-2">{renderRoleBadges(rp)}</div>
                    </div>
                  </div>
                </td>
                <td className="min-w-[170px] p-5">
                  <div className="font-bold text-[var(--admin-text-primary)]">{t('rpManagement.stewardsCount', { count: formatNumber(rp.stewards.length) })}</div>
                  {renderStewardChips(rp)}
                </td>
                <td className="min-w-[170px] p-5">
                  <div className="font-bold text-[var(--admin-text-primary)]">{t('rpManagement.unitsCount', { count: formatNumber(rp.managedUnits.length) })}</div>
                  {renderUnitChips(rp)}
                </td>
                {canViewPartnerNetwork && (
                  <td className="min-w-[170px] p-5">
                    <div className="break-words font-bold text-amber-700">{formatIntegerCurrency(rp.summary?.totalProfit || 0)}</div>
                    <div className="mt-1 text-xs leading-5 text-[var(--admin-text-muted)]">{t('rpManagement.revenue', { amount: formatIntegerCurrency(rp.summary?.totalRevenue || 0) })}</div>
                  </td>
                )}
                {canViewPartnerNetwork && <td className="min-w-[150px] p-5">
                  <div className="font-bold text-emerald-700">{rp.avgCompliance}%</div>
                  {canViewPartnerNetwork && (
                    <div className="mt-1 text-xs leading-5 text-[var(--admin-text-muted)]">{t('rpManagement.volume', { volume: formatIntegerNumber(rp.summary?.totalVolume || 0) })}</div>
                  )}
                </td>}
                <td className="sticky right-0 min-w-[210px] bg-[var(--admin-surface)] p-5 text-right shadow-[-12px_0_18px_rgba(15,23,42,0.08)]">
                  {renderActions(rp)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="grid gap-4 lg:hidden">
        {loading && (
          <div className="admin-card rounded-xl p-8 text-center">
            {t('rpManagement.loading')}
          </div>
        )}

        {!loading && filteredRps.length === 0 && (
          <div className="admin-card rounded-xl p-8 text-center">
            {t('rpManagement.empty')}
          </div>
        )}

        {!loading && filteredRps.map((rp) => (
          <article key={rp.id} className="admin-card rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-cyan-100 font-black uppercase text-cyan-800">
                {getUserDisplayName(rp).slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-mono text-xs text-[var(--admin-text-muted)]">ID: {rp.id}</p>
                <Link to={`/pa/${rp.id}`} className="block break-words text-base font-black text-[var(--admin-text-primary)]">
                  {getUserDisplayName(rp)}
                </Link>
                <p className="break-words text-sm text-[var(--admin-text-muted)]">{getUserPhone(rp)}</p>
                <div className="mt-3">{renderRoleBadges(rp)}</div>
              </div>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="admin-muted-panel rounded-lg p-3">
                <p className="text-xs font-bold uppercase text-[var(--admin-text-muted)]">{t('rpManagement.table.stewards')}</p>
                <p className="mt-1 text-lg font-black text-[var(--admin-text-primary)]">{t('rpManagement.stewardsCount', { count: formatNumber(rp.stewards.length) })}</p>
                {renderStewardChips(rp)}
              </div>

              <div className="admin-muted-panel rounded-lg p-3">
                <p className="text-xs font-bold uppercase text-[var(--admin-text-muted)]">{t('rpManagement.table.devices')}</p>
                <p className="mt-1 text-lg font-black text-[var(--admin-text-primary)]">{t('rpManagement.unitsCount', { count: formatNumber(rp.managedUnits.length) })}</p>
                {renderUnitChips(rp)}
              </div>

              {canViewPartnerNetwork && (
                <div className="admin-muted-panel rounded-lg p-3">
                  <p className="text-xs font-bold uppercase text-[var(--admin-text-muted)]">{t('rpManagement.table.monthProfit')}</p>
                  <p className="mt-1 break-all text-lg font-black text-amber-700">{formatIntegerCurrency(rp.summary?.totalProfit || 0)}</p>
                  <p className="mt-1 text-xs leading-5 text-[var(--admin-text-muted)]">{t('rpManagement.revenue', { amount: formatIntegerCurrency(rp.summary?.totalRevenue || 0) })}</p>
                </div>
              )}

              {canViewPartnerNetwork && <div className="admin-muted-panel rounded-lg p-3">
                <p className="text-xs font-bold uppercase text-[var(--admin-text-muted)]">{t('rpManagement.table.compliance')}</p>
                <p className="mt-1 text-lg font-black text-emerald-700">{rp.avgCompliance}%</p>
                {canViewPartnerNetwork && (
                  <p className="mt-1 text-xs leading-5 text-[var(--admin-text-muted)]">{t('rpManagement.volume', { volume: formatIntegerNumber(rp.summary?.totalVolume || 0) })}</p>
                )}
              </div>}
            </div>

            <div className="mt-4">
              {renderActions(rp, true)}
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}
